import { getRowBatch, pluralise, rowState } from 'src/views/Batches/useBatches'
import { lanesOf } from 'src/views/Batches/BatchParts'
import { shortDay } from 'src/views/NewAnalysis/model/candidates'

// ----------------------------------------------------------------------
// Pure helpers behind Home's two cards, "Ready to analyse" and "In progress".
// ----------------------------------------------------------------------

// At most this many rows per card; the rest fold into "+N more".
export const MAX_ROWS = 5

const timeOf = (row) => {
  const t = new Date(row?.created_at).getTime()
  return Number.isFinite(t) ? t : 0
}

// ------------------------------------------------------------ Ready to analyse

const READY_TYPE_LABEL = {
  fastq: 'Paired-end FASTQ',
  ont_bam: 'ONT BAM',
  pacbio_bam: 'PacBio HiFi',
  vcf: 'VCF',
}

/** "Paired-end FASTQ", "ONT BAM", "PacBio HiFi" or "VCF" for a file type of /file/list. */
export const readyTypeLabel = (fileType) =>
  READY_TYPE_LABEL[fileType] || (fileType ? String(fileType).replace(/_/g, ' ').toUpperCase() : '')

/** "3 waiting", or nothing when none is. */
export const readyCountText = (count) => (count > 0 ? `${count} waiting` : '')

// ------------------------------------------------------------ In progress

const COHORT_TYPES = ['GENESUS-COHORT', 'EXOMEDEPTH-COHORT']

/**
 * The sample name Analysis History shows for a row (its Sample column), so a row
 * here can be found there by the same text.
 */
export const rowSampleLabel = (row) => {
  if (!row) return ''
  if (row.sample_alias) return row.display_sample_name || row.sample_alias
  if (COHORT_TYPES.includes(row.analysis_type)) return row.analysis_parameters?.cohort_name || 'Unknown Cohort'
  return row.display_sample_name || row.sample_name || ''
}

/** A short input or sequencing label for a row: WES, WGS, CES, ONT, PacBio, VCF... */
export const shortRowType = (row) => {
  const type = row?.analysis_type || ''
  const tags = Array.isArray(row?.tags) ? row.tags : []
  const annotation = row?.analysis_parameters?.annotation || {}
  if (type === 'ONT_BAM') return 'ONT'
  if (type === 'PACBIO_BAM') return 'PacBio'
  if (type === 'VCF') return 'VCF'
  if (type === 'CARRIER') return 'Carrier'
  if (COHORT_TYPES.includes(type)) return 'Cohort'
  if (annotation.type === 'WholeGenome' || tags.includes('WGS')) return 'WGS'
  if (annotation.targetedType === 'Whole Exome' || tags.includes('WES')) return 'WES'
  if (annotation.targetedType === 'Clinical Exome' || tags.includes('CES')) return 'CES'
  if (type === 'FASTQ') return 'FASTQ'
  if (!type) return ''
  const words = type.replace(/[_-]+/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The phase a running row is in, from its lanes: "Alignment", "Calling +1" when
 * more run at once, "Starting" before the first one, "Finishing" after the last.
 */
export const currentStepOf = (row) => {
  const lanes = lanesOf(row)
  const running = lanes.filter((lane) => lane.state === 'running' && lane.title)
  if (running.length) return running.length > 1 ? `${running[0].title} +${running.length - 1}` : running[0].title
  if (lanes.length && lanes.every((lane) => lane.state === 'done')) return 'Finishing'
  if (lanes.some((lane) => lane.state === 'done')) return 'Running'
  return 'Starting'
}

/** Compact running time: "<1m", "48m", "1h30", "2h", "3d 4h". */
export const formatRunning = (ms) => {
  if (!Number.isFinite(ms)) return ''
  const minutes = Math.floor(Math.max(0, ms) / 60000)
  if (minutes < 1) return '<1m'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return minutes % 60 ? `${hours}h${String(minutes % 60).padStart(2, '0')}` : `${hours}h`
  const days = Math.floor(hours / 24)
  return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`
}

// The backend reports failed runs as running, for good (see rowState), so the
// list holds runs "running" for months. The card lists the ones started in this
// window, where a run can still be going; older ones stay in Analysis History
// with the status the server gives them. A run in the window is shown as it is,
// however long it has been running: there is no Failed state to show.
export const RUNNING_WINDOW_DAYS = 7
const RUNNING_WINDOW_MS = RUNNING_WINDOW_DAYS * 24 * 60 * 60 * 1000

const inWindow = (startedAt, now) => startedAt > 0 && now - startedAt <= RUNNING_WINDOW_MS

/**
 * What the In progress card lists: unfinished batches (newest first, as
 * groupBatches sorts them), then running analyses that are not part of a batch,
 * newest first; both only when started within RUNNING_WINDOW_DAYS.
 */
export const inProgressOf = (rows = [], batches = [], now = Date.now()) => ({
  batches: batches.filter((batch) => !batch.finished && inWindow(batch.lastStartedAt, now)),
  singles: rows
    .filter((row) => rowState(row) === 'running' && !getRowBatch(row) && inWindow(timeOf(row), now))
    .sort((a, b) => timeOf(b) - timeOf(a)),
})

/**
 * The card's rows, batches first, capped at MAX_ROWS, and how many did not fit:
 * in all, and of each kind (the batches left out are on All batches, the
 * analyses in Analysis History).
 */
export const inProgressRows = ({ batches, singles }, limit = MAX_ROWS) => {
  const all = [
    ...batches.map((batch) => ({ key: `batch-${batch.id}`, type: 'batch', batch })),
    ...singles.map((row) => ({ key: `analysis-${row.analysis_id}`, type: 'single', row })),
  ]
  const rows = all.slice(0, limit)
  const shownBatches = rows.filter((item) => item.type === 'batch').length
  const moreBatches = batches.length - shownBatches
  const moreSingles = singles.length - (rows.length - shownBatches)
  return { rows, more: Math.max(0, all.length - limit), moreBatches, moreSingles }
}

/** "2 running · 1 batch", "3 batches", or nothing. */
export const inProgressCountText = ({ batches, singles }) =>
  [
    singles.length ? `${singles.length} running` : null,
    batches.length ? pluralise(batches.length, 'batch', 'batches') : null,
  ]
    .filter(Boolean)
    .join(' · ')

/**
 * The newest finished analysis, for the empty card. The list has no finish time,
 * so "newest" is by start time and the line says when it started.
 */
export const lastFinishedOf = (rows = []) => {
  let best = null
  rows.forEach((row) => {
    if (rowState(row) !== 'done') return
    if (!best || timeOf(row) > timeOf(best)) best = row
  })
  return best ? { label: rowSampleLabel(best), startedAt: timeOf(best) } : null
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** "just now", "12 min ago", "2 h ago", "3 d ago", or "on 28 Aug" once it is a week old. */
export const startedAgo = (ms, now = Date.now()) => {
  if (!ms) return ''
  const elapsed = Math.max(0, now - ms)
  if (elapsed >= 7 * DAY_MS) return `on ${shortDay(ms)}`
  if (elapsed >= DAY_MS) return `${Math.floor(elapsed / DAY_MS)} d ago`
  if (elapsed >= HOUR_MS) return `${Math.floor(elapsed / HOUR_MS)} h ago`
  if (elapsed >= MINUTE_MS) return `${Math.floor(elapsed / MINUTE_MS)} min ago`
  return 'just now'
}

/** "Last finished: NA24385, started 2 h ago". */
export const lastFinishedText = (finished, now = Date.now()) =>
  finished && finished.label ? `Last finished: ${finished.label}, started ${startedAgo(finished.startedAt, now)}` : ''

// ------------------------------------------------------------ keeping running rows fresh

// What can change on a running row: its status and its phases.
export const rowSignature = (row) =>
  `${row?.status || ''}|${lanesOf(row)
    .map((lane) => lane.state)
    .join(',')}`

/**
 * What Home knows about its single (not batched) analyses, for historyDiffers:
 * their signatures by id, and the newest start time of any row.
 */
export const knownRowsOf = (rows = []) => {
  const signatures = new Map()
  let newest = 0
  rows.forEach((row) => {
    const t = timeOf(row)
    if (t > newest) newest = t
    if (row?.analysis_id === undefined || row?.analysis_id === null || getRowBatch(row)) return
    signatures.set(String(row.analysis_id), rowSignature(row))
  })
  return { signatures, newest }
}

/**
 * True when rows fetched by Analysis History (which polls on its own) say more
 * than Home's list: a single analysis moved on, or one started after the newest
 * Home knows of. Batched rows are left to the batches' own polling.
 */
export const historyDiffers = (historyRows, known) => {
  if (!known || !Array.isArray(historyRows)) return false
  return historyRows.some((row) => {
    const id = row?.analysis_id
    if (id === undefined || id === null) return false
    const mine = known.signatures.get(String(id))
    if (mine !== undefined) return mine !== rowSignature(row)
    return timeOf(row) > known.newest
  })
}
